// Senha própria do Diário (0.66, B4/B5): tipos de senha (senha, PIN, padrão), criação e código de recuperação.
import { useState } from 'react';
import { Alert, Platform, Text, View } from 'react-native';
import { senhaValida, type TipoSenhaDiario } from '../../store/diario';
import { Padrao } from '../Padrao';
import { Btn, Chip, Field, Screen } from '../ui';
import { C, alpha } from '../theme';
import { copiarTexto } from '../../lib/copiar';
import { semBloqueio } from '../../lib/ausencia';
import { t } from '../../lib/i18n';

/** Mostra o código de recuperação para a pessoa guardar (ao criar o diário ou ao gerar um novo). */
export function CodigoRecuperacao({ codigo, cor, onPronto, titulo, embutido }: { codigo: string; cor: string; onPronto: () => void; titulo?: string; embutido?: boolean }) {
  const [copiado, setCopiado] = useState(false);
  async function salvarArquivo() {
    const texto = t('My Planner · Código de recuperação do Diário') + '\n\n' + codigo + '\n\n' + t('Com este código você recupera o diário se esquecer a senha ou trocar de dispositivo. Guarde em lugar seguro e não mostre a ninguém.');
    try {
      if (Platform.OS === 'web') { await copiarTexto(texto); setCopiado(true); return; }
      const { File, Paths } = require('expo-file-system') as typeof import('expo-file-system');
      const Sharing = require('expo-sharing') as typeof import('expo-sharing');
      const f = new File(Paths.cache, 'codigo-recuperacao-diario.txt');
      try { if (f.exists) f.delete(); } catch { /* segue */ }
      f.write(texto);
      await semBloqueio(() => Sharing.shareAsync(f.uri, { mimeType: 'text/plain', dialogTitle: t('Salvar o código de recuperação') }));
    } catch (e: any) { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); }
  }
  const conteudo = (
    <>
      <View style={{ alignItems: 'center', gap: 6, marginVertical: 10 }}>
        <Text style={{ fontSize: 48 }}>🔑</Text>
        <Text style={{ color: C.text, fontWeight: '800', fontSize: 17, textAlign: 'center' }}>{t('Guarde este código')}</Text>
        <Text style={{ color: C.sub, textAlign: 'center' }}>{t('Com ele você recupera o diário se esquecer a senha ou trocar de dispositivo.')}</Text>
      </View>
      <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 16, padding: 18, borderWidth: 1, borderColor: alpha(cor, 0.45) }}>
        <Text selectable style={{ color: C.text, fontSize: 22, fontWeight: '900', letterSpacing: 1.5, textAlign: 'center', fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace' }}>{codigo}</Text>
      </View>
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <Btn cor={cor} outline style={{ flex: 1 }} label={copiado ? t('Copiado ✓') : '📋 ' + t('Copiar')} onPress={async () => { await copiarTexto(codigo); setCopiado(true); }} />
        <Btn cor={cor} outline style={{ flex: 1 }} label={'💾 ' + t('Salvar em arquivo / no Drive')} onPress={salvarArquivo} />
      </View>
      <Text style={{ color: C.warn, fontSize: 12 }}>{t('O código não fica guardado em lugar nenhum que o suporte possa ver. Sem a senha e sem o código, o diário não pode ser recuperado.')}</Text>
      <Btn cor={cor} label={t('Já guardei')} onPress={onPronto} />
    </>
  );
  return embutido
    ? <View style={{ padding: 16, gap: 12 }}>{conteudo}</View>
    : <Screen title={titulo ?? t('Código de recuperação')} onBack={onPronto}>{conteudo}</Screen>;
}

/** Campo da senha própria do diário, conforme o tipo (senha, PIN ou desenho padrão). */
export function CampoSenhaDiario({ tipo, valor, onValor, ph, cor, onPronto, erro }: { tipo: TipoSenhaDiario; valor: string; onValor: (v: string) => void; ph: string; cor: string; onPronto?: (v: string) => void; erro?: boolean }) {
  if (tipo === 'padrao') return (
    <View style={{ alignItems: 'center', gap: 6 }}>
      <Text style={{ color: erro ? C.danger : C.sub }}>{ph}</Text>
      <Padrao tamanho={240} cor={cor} erro={erro} onFim={seq => { onValor(seq); onPronto?.(seq); }} />
    </View>
  );
  if (tipo === 'pin') return <Field value={valor} onChangeText={v => onValor(v.replace(/\D/g, '').slice(0, 6))} placeholder={ph} secureTextEntry keyboardType="number-pad" maxLength={6} onSubmitEditing={() => onPronto?.(valor)} />;
  return <Field value={valor} onChangeText={onValor} placeholder={ph} secureTextEntry onSubmitEditing={() => onPronto?.(valor)} />;
}

const TIPOS: [TipoSenhaDiario, string][] = [['senha', 'Senha (letras e números)'], ['pin', 'PIN (só números, 4 a 6)'], ['padrao', 'Padrão (desenho)']];
const tiposDoAparelho = () => TIPOS.filter(([k]) => k !== 'padrao' || Platform.OS === 'android');
const regraDoTipo = (tipo: TipoSenhaDiario) => (tipo === 'pin' ? t('O PIN precisa ter de 4 a 6 números.') : tipo === 'padrao' ? t('Ligue pelo menos 4 pontos.') : t('A senha do diário precisa ter 6 caracteres ou mais.'));

/** Escolher o tipo e criar a senha própria do diário (digitada duas vezes). */
export function CriarSenhaDiario({ cor, onPronta, botao }: { cor: string; onPronta: (senha: string, tipo: TipoSenhaDiario) => void; botao: string }) {
  const [tipo, setTipo] = useState<TipoSenhaDiario>('senha');
  const [s1, setS1] = useState(''); const [s2, setS2] = useState('');
  const [msg, setMsg] = useState('');
  const mudar = (x: TipoSenhaDiario) => { setTipo(x); setS1(''); setS2(''); setMsg(''); };
  const pronto = (a = s1, b = s2) => {
    if (!senhaValida(a, tipo)) return setMsg(regraDoTipo(tipo));
    if (a !== b) { setS2(''); if (tipo === 'padrao') setS1(''); return setMsg(tipo === 'padrao' ? t('Os desenhos não conferem. Comece de novo.') : t('As senhas não conferem.')); }
    onPronta(a, tipo);
  };
  return (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {tiposDoAparelho().map(([k, l]) => <Chip key={k} small cor={cor} on={tipo === k} label={t(l)} onPress={() => mudar(k)} />)}
      </View>
      {tipo === 'padrao' ? (
        !s1 ? <CampoSenhaDiario tipo="padrao" cor={cor} valor={s1} onValor={v => { if (senhaValida(v, 'padrao')) { setS1(v); setMsg(''); } else setMsg(regraDoTipo('padrao')); }} ph={t('Desenhe um padrão ligando pelo menos 4 pontos.')} />
          : <CampoSenhaDiario tipo="padrao" cor={cor} valor={s2} onValor={setS2} ph={t('Desenhe de novo para confirmar.')} onPronto={v => pronto(s1, v)} />
      ) : (<>
        <CampoSenhaDiario tipo={tipo} cor={cor} valor={s1} onValor={setS1} ph={tipo === 'pin' ? t('Crie o PIN do diário (4 a 6 números)') : t('Crie a senha do diário (6 ou mais)')} />
        <CampoSenhaDiario tipo={tipo} cor={cor} valor={s2} onValor={setS2} ph={tipo === 'pin' ? t('Repita o PIN') : t('Repita a senha')} onPronto={v => pronto(s1, v)} />
      </>)}
      {!!msg && <Text style={{ color: C.danger }}>{msg}</Text>}
      {tipo !== 'padrao' && <Btn cor={cor} label={botao} onPress={() => pronto()} />}
    </View>
  );
}

